import React, { useEffect, useState } from "react";
import { IoDownloadOutline, IoEyeOutline, IoAddOutline } from "react-icons/io5";
import { useNavigate, useParams } from "react-router-dom";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import { motion } from "framer-motion";
import axios from "axios";
import { useUser } from "../../context/UserContext";
import api from "../../utils/api";

const Payslips = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [payslips, setPayslips] = useState([]);
  const { empid } = useParams();
  const payslipsPerPage = 8;
  const navigate = useNavigate();

  // Add user context
  const { currentUser } = useUser();
  const baseRoute = "/hr";

  const handleClick = (payid) => {
    navigate(`${baseRoute}/employees/View/${payid}`);
  };

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await api.get(`/userpayslip/${empid}`);
        console.log("Fetched payslips:", response.data); // Log the fetched data
        setPayslips(response.data);
      } catch (error) {
        console.error("Error fetching employees:", error);

        setPayslips([]); // Set empty array on error
      }
    };

    fetchEmployees();
  }, []);

  const handleDownload = async (payid, salaryPeriod, employeeId) => {
    try {
      const response = await api.get(`/payslip/download/${payid}`, {
        responseType: "blob", // Important for binary data like PDFs
      });

      // Create a blob from the PDF stream
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `${employeeId}_payslip_${salaryPeriod}.pdf`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Failed to download PDF:", error);
    }
  };

  const totalPages = Math.ceil(payslips.length / payslipsPerPage);
  const startIndex = (currentPage - 1) * payslipsPerPage;
  const currentPayslips = payslips.slice(
    startIndex,
    startIndex + payslipsPerPage
  );

  return (
    <div className="p-4 sm:p-6 w-full mx-auto">
      {/* Header Section */}
      <motion.div
        className="flex flex-col sm:flex-row justify-between sm:gap-2 gap-4 mt-1 mb-4"
        transition={{ duration: 0.4, type: "spring" }}
      >
        <div className="min-w-0">
          <h2 className="text-xl xs:text-2xl font-bold truncate">
            Employee Payslips
          </h2>
        </div>
        {/* <motion.button
          onClick={() => navigate("/admin/addpayslip")}
          className="inline-flex items-center gap-2 bg-slate-800 text-white px-6 py-2.5 rounded-full hover:bg-slate-700 
                       transition-all duration-200 shadow-lg hover:shadow-xl active:shadow-md"
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
        >
          <IoAddOutline className="text-lg" />
          <span>Add New Payslip</span>
        </motion.button> */}
      </motion.div>

      {/* Desktop Table View */}
      <motion.div
        className="hidden sm:block bg-white rounded-2xl shadow-md overflow-hidden"
        transition={{ duration: 0.5 }}
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr className="text-left text-slate-600">
                <th className="px-6 py-4 font-semibold text-slate-600 text-left">
                  S.No
                </th>
                <th className="px-6 py-4 font-semibold">Salary period</th>
                <th className="px-6 py-4 font-semibold">Amount</th>
                <th className="px-6 py-4 font-semibold">Status</th>
                <th className="px-6 py-4 font-semibold">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {currentPayslips.map((payslip, index) => (
                <tr
                  key={payslip.payid}
                  className="hover:bg-slate-50/50 transition-colors duration-150"
                >
                  <td className="px-6 py-4 text-slate-600">
                    {(currentPage - 1) * payslipsPerPage + index + 1}
                  </td>
                  <td className="px-6 py-4">{payslip.salaryPeriod}</td>
                  <td className="px-6 py-4 font-medium">
                    {payslip.netPayable}
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-700`}
                    >
                      Paid
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-4">
                      <motion.button
                        onClick={() => handleClick(payslip.payid)}
                        className="text-slate-600 hover:text-blue-600 transition-colors"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        title="View Payslip"
                      >
                        <IoEyeOutline className="text-xl" />
                      </motion.button>
                      <motion.button
                        onClick={() =>
                          handleDownload(
                            payslip.payid,
                            payslip.salaryPeriod,
                            payslip.employeeId
                          )
                        }
                        className="text-slate-600 hover:text-blue-600 transition-colors"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        title="Download Payslip"
                      >
                        <IoDownloadOutline className="text-xl" />
                      </motion.button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-slate-100">
          <Stack spacing={2} className="flex justify-end">
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(_, page) => setCurrentPage(page)}
              variant="outlined"
              shape="rounded-full"
              size="large"
              showFirstButton
              showLastButton
              sx={{
                "& .MuiPaginationItem-root": {
                  color: "#475569",
                  borderColor: "#e2e8f0",
                },
                "& .Mui-selected": {
                  backgroundColor: "#475569 !important",
                  color: "#ffffff",
                },
              }}
            />
          </Stack>
        </div>
      </motion.div>

      {/* Mobile Card View */}
      <motion.div
        className="sm:hidden space-y-4"
        transition={{ duration: 0.5 }}
      >
        {currentPayslips.map((payslip) => (
          <motion.div
            key={payslip.id}
            className="bg-white p-4 rounded-xl shadow-sm border border-slate-100"
            whileHover={{ scale: 1.01 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-lg font-medium text-slate-800">
                  {payslip.month}
                </span>
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium
                  ${
                    payslip.status === "Paid"
                      ? "bg-green-100 text-green-700"
                      : "bg-yellow-100 text-yellow-700"
                  }`}
                >
                  {payslip.status}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-xl font-semibold text-slate-700">
                  {payslip.salary}
                </span>
                <div className="flex gap-3">
                  <motion.button
                    onClick={() => handleClick(payslip.payid)}
                    className="p-2 text-slate-600 hover:bg-slate-50 rounded-lg transition-colors"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    title="View Payslip"
                  >
                    <IoEyeOutline className="text-xl font-bold" />
                  </motion.button>
                  <motion.button
                    onClick={() => handleDownload(payslip.payid)}
                    className="p-2 text-slate-600 hover:bg-slate-50 rounded-lg transition-colors"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    title="Download Payslip"
                  >
                    <IoDownloadOutline className="text-xl" />
                  </motion.button>
                </div>
              </div>
            </div>
          </motion.div>
        ))}

        {/* Mobile Pagination */}
        <div className="mt-6 flex justify-center">
          <Stack spacing={2}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(_, page) => setCurrentPage(page)}
              variant="outlined"
              shape="rounded"
              size="medium"
              sx={{
                "& .MuiPaginationItem-root": {
                  color: "#475569",
                  borderColor: "#e2e8f0",
                },
                "& .Mui-selected": {
                  backgroundColor: "#475569 !important",
                  color: "#ffffff",
                },
              }}
            />
          </Stack>
        </div>
      </motion.div>
    </div>
  );
};

export default Payslips;
